<template>
  <div class="step">
    <div class="step-header">
    
      <div class="step-content">
        <AppButtonFlat
          color="primary"
          block
          variant="outlined"
          prepend-icon="mdi-plus"
          @click="openAddPackageModal"
        >
          Create package
        </AppButtonFlat>
        <!-- <p class="desc">
          Create packages/pricing templates for your services. You can customize details later.
        </p> -->
      </div>
    </div>
    <h2 class="title text-success">
      Create packages/pricing templates for your services. You can customize details
      later.
    </h2>

    <div class="form-field">
      <div v-if="serverError" class="api-error">{{ serverError }}</div>
    </div>

    <div class="package-list">
      <div v-for="(pkg, index) in filteredPackages" :key="index" class="package-item">
        <div class="package-card">
          <div class="package-header">
            <div class="package-name-section">
              <h3 class="package-title text-capitalize">{{ pkg.name }}</h3>
              <p class="package-highlight">{{ pkg.highlight }}</p>
            </div>
            <div class="package-actions">
              <v-btn
                size="small"
                variant="text"
                icon="mdi-delete"
                color="error"
                @click="deletePackage(index)"
                class="delete-btn"
              />
            </div>
          </div>

          <div class="package-features">
            <div class="features-list">
              <div
                v-for="feature in pkg.features.split(',')"
                :key="feature"
                class="feature-item"
              >
                <v-icon size="16" color="#10b981" class="feature-icon"
                  >mdi-check-circle</v-icon
                >
                <span class="feature-text">{{ feature.trim() }}</span>
              </div>
            </div>
          </div>

          <div class="package-pricing">
            <div class="pricing-header">
              <span class="pricing-label">Pricing Options</span>
            </div>
            <div class="pricing-grid">
              <div v-for="price in pkg.pricing" :key="price.type" class="pricing-card">
                <div class="pricing-type">{{ formatPriceType(price.type) }}</div>
                <div class="pricing-amount">${{ price.amount }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="selected-hint">
      Total packages: <strong>{{ packages.length }}</strong>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import AddPackageModal from "@/modules/vendor/components/profile-setup/AddPackageModal.vue";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";

const props = defineProps<{
  current_step?: number;
  errors?: Record<string, any> | null;
  profileInfo?: any;
}>();

type PackagePricing = {
  type: string;
  amount: number;
};

type Package = {
  id?: number | string;
  name: string;
  highlight: string;
  features: string;
  pricing: PackagePricing[];
};

const modal = useModalStore();

const packages = ref<Package[]>([]);
const search = ref("");
const serverError = ref("");

watch(
  () => props.profileInfo,
  (info) => {
    const apiPkg = info?.service_packages || null;
    if (!apiPkg || typeof apiPkg !== "object") return;
    if (packages.value.length) return; // don't overwrite user edits

    const name = String(apiPkg?.name || "").trim();
    if (!name) return;

    const highlight = String(apiPkg?.highlight || "").trim();
    const features = String(apiPkg?.feature || apiPkg?.features || "").trim();
    const rawPricing = Array.isArray(apiPkg?.pricing) ? apiPkg.pricing : [];
    const pricing: PackagePricing[] = rawPricing
      .map((p: any) => {
        const type = String(p?.type || "").trim();
        const amount = Number(p?.amount ?? 0);
        if (!type) return null;
        return {
          type,
          amount: Number.isFinite(amount) ? amount : 0,
        } as PackagePricing;
      })
      .filter(Boolean) as PackagePricing[];

    packages.value = [
      {
        id: apiPkg?.id,
        name,
        highlight,
        features,
        pricing,
      },
    ];
  },
  { immediate: true }
);

const filteredPackages = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return packages.value;
  return packages.value.filter((pkg) => {
    return (
      pkg.name.toLowerCase().includes(q) ||
      pkg.highlight.toLowerCase().includes(q) ||
      pkg.features.toLowerCase().includes(q)
    );
  });
});

// Map props.errors to a friendly serverError message to display under the create button
watch(
  () => props.errors,
  (errs: any) => {
    serverError.value = "";
    if (!errs) return;

    const root = errs?.errors && typeof errs.errors === "object" ? errs.errors : errs;
    const packageListErr = root?.package_list ?? root?.packages;
    if (packageListErr) {
      serverError.value = Array.isArray(packageListErr)
        ? packageListErr.join(" ")
        : String(packageListErr);
      return;
    }

    if (typeof errs.message === "string" && errs.message.trim()) {
      serverError.value = errs.message;
      return;
    }
  },
  { immediate: true }
);

function getValues() {
  return {
    id: packages.value?.[0]?.id,
    packages: packages.value,
    search: search.value,
  };
}

defineExpose({ getValues });

function openAddPackageModal() {
  modal.open(
    AddPackageModal,
    {
      onAdd: (pkg: Package) => {
        packages.value = [...packages.value, pkg];
      },
    },
    { fullscreen: true, title: "Add Package", showHeader: false }
  );
}

function deletePackage(index: number) {
  packages.value.splice(index, 1);
}

function formatPriceType(type: string): string {
  const typeMap: Record<string, string> = {
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
    year: "Yearly",
  };
  return typeMap[type] || type;
}
</script>

<style scoped>
.step {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.step-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
}

.step-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  background: rgba(232, 97, 47, 0.1);
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 4px;
  letter-spacing: -0.025em;
}

.desc {
  margin: 0;
  color: var(--color-subtitle-light);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 400;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
}

.label-icon {
  flex-shrink: 0;
}

.package-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 6px;
}

.package-item {
  border: none;
  border-radius: 16px;
  overflow: hidden;
}

.package-card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.package-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.package-name-section {
  flex: 1;
}

.package-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.package-highlight {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.package-actions {
  display: flex;
  gap: 4px;
}

.delete-btn {
  opacity: 0.6;
  transition: opacity 0.2s ease;
}

.delete-btn:hover {
  opacity: 1;
}

.package-features {
  margin-bottom: 24px;
  flex: 1;
}

.features-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.feature-icon {
  flex-shrink: 0;
  margin-top: 2px;
}

.feature-text {
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  font-weight: 400;
}

.package-pricing {
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
}

.pricing-header {
  margin-bottom: 16px;
}

.pricing-label {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  gap: 12px;
}

.pricing-card {
  background: linear-gradient(135deg, #f3f4f6 0%, #ffffff 100%);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  text-align: center;
  transition: all 0.2s ease;
}

.pricing-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 1px 3px rgba(59, 130, 246, 0.1);
}

.pricing-type {
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  margin-bottom: 4px;
  text-transform: capitalize;
}

.pricing-amount {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.selected-hint {
  margin-top: 4px;
  color: #6b7280;
  font-size: 13px;
}

.api-error {
  color: #dc2626; /* red-600 */
  font-size: 13px;
  margin-top: 8px;
}
</style>
